import { AsyncPipe,  NgClass,  TitleCasePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, ChangeDetectorRef, OnDestroy, input, output, inject, viewChild, signal, computed, effect } from '@angular/core';
import { ClientService, TimeconversionService, ToastMessageService, StaffStoreService } from '@fitness-force/api';
import { ClientStatus, ExternalProviderCategory } from '@fitness-force/enum';
import type { child, ClientById, ExternalProvider, STAGE_MASTER_MODEL, TENANTSINTERFACE } from '@fitness-force/models';
import { ShortNamePipe } from '@fitness-force/pipes/short-name';
import { BookAppointmentComponent, timeLineService } from '@fitness-force/shared';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { STAGE_MASTER_STATE, TENANTS_INFO } from '@fitness-force/state';
import { AvatarModule } from 'primeng/avatar';
import { MessageModule } from 'primeng/message';

import { PopoverModule, Popover } from 'primeng/popover';
import { SkeletonModule } from 'primeng/skeleton';
import { BehaviorSubject, Observable, map, combineLatest, Subject, of } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ProspectListActionsComponent } from '../prospect-list-actions/prospect-list-actions.component';
import { FeatureFlagService } from '@fitness-force/shared-utils';
import { CONSTANTS_DATA } from '@fitness-force/constants';
import { ClientProfileDetailsTabComponent } from '../client-profile-details-tab/client-profile-details-tab.component';
import { StyleClassModule } from 'primeng/styleclass';
import { TooltipModule } from 'primeng/tooltip';
import { SendBulkEmailComponent, SendBulkSmsComponent, SendWhatsappComponent } from '@fitness-force/campaigns';
import { SelectModule } from 'primeng/select';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';

@Component({
  selector: 'fitness-force-client-profile-left-side',
  templateUrl: './client-profile-left-side.component.html',
  styleUrls: ['./client-profile-left-side.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    FormsModule,
    SkeletonModule,
    AvatarModule,
    MessageModule,
    PopoverModule,
    StyleClassModule,
    ShortNamePipe,
    ProspectListActionsComponent,
    BookAppointmentComponent,
    SendBulkSmsComponent,
    SendBulkEmailComponent,
    SendWhatsappComponent,
    TooltipModule,
    SelectModule,
    ClientProfileDetailsTabComponent,
    ButtonModule,
    NgClass,
    TranslateModule, AsyncPipe, TitleCasePipe]
  })
  
export class ClientProfileLeftSideComponent implements OnInit, OnDestroy {
  private readonly clientService = inject(ClientService);
  private readonly store = inject(Store);
  private readonly timeService = inject(TimeconversionService);
  private readonly cd = inject(ChangeDetectorRef);
  private readonly translate = inject(TranslateService);
  readonly toastMessageService = inject(ToastMessageService);
  private readonly staffStoreService = inject(StaffStoreService);
  private readonly featureFlagService = inject(FeatureFlagService);

  readonly client = input<ClientById>({} as ClientById);
  readonly tenantId = input(0);
  readonly showSkeleton$ = input(new BehaviorSubject<boolean>(true));
  readonly showCloseIcon$ = input(new BehaviorSubject<boolean>(true));
  /** When true (inbox context), shows a "View Full Profile" button */
  readonly isInboxContext = input<boolean>(false);
  readonly closeClientSidebar = output<boolean>();
  /** Emitted when the user clicks "View Full Profile" — parent handles opening the sidebar */
  readonly openFullProfile = output<void>();
  readonly changeEvent = output();

  // Signals for component state
  readonly currentTenantId = signal(0);
  readonly showClientDetails = signal(false);
  readonly showClientSidebar = signal(false);
  readonly allTenants = signal<any[]>([]);
  readonly selectedTenant = signal<any>(null);
  readonly clientIds = signal<number[]>([]);
  readonly showOrganization = signal(false);
  readonly showAddCreditButton = signal(false);
  readonly showSendSMs = signal(false);
  readonly showSendEmail = signal(false);
  readonly currentTenantClient = signal(true);
  readonly showBookAppointmentPopup = signal(false);
  readonly attachmentsModal = signal(false);
  readonly selectedClientInfo = signal<{name: string, value: string }>({name: '', value: ''});
  readonly parentClient = signal<ClientById>({} as ClientById);
  readonly childs = signal<child[]>([]);
  readonly relationDetails = signal<any[]>([]);
  readonly showDeleteDigitalPass = signal(false);
  readonly currentStageStatus = signal<string | null>(null);

  // Legacy BehaviorSubjects for backward compatibility
  readonly allClients$ = new BehaviorSubject<ClientById>({} as ClientById);
  readonly toggleSendEmailDialog$ = new BehaviorSubject<boolean>(false);
  readonly toggleCreateSmsDialog$ = new BehaviorSubject<boolean>(false);
  readonly toggleSendWhatsAppDialog$ = new BehaviorSubject<boolean>(false);
  readonly isBlackListed = new BehaviorSubject<boolean>(false);
  readonly toggleEditProfileDialog$ = new BehaviorSubject<boolean | null>(false);
  readonly SelectedClient = new BehaviorSubject<ClientById>({} as ClientById); 
  readonly clientInfos = new BehaviorSubject<any[]>([]);

  // Loading states
  readonly isLoadingClientDetails = signal(false);

  selectedMenu: any;
  getAccessTenantList$: Observable<any> | any | null;
  
  readonly profileActionPopover = viewChild<Popover>('profileAction');
  
  // Add destroy subject for cleanup
  private readonly destroy$ = new Subject<void>();

  constructor() {
    // Sync client signal with BehaviorSubject for columnData
    effect(() => {
      const client = this.client();
      if (client && client.id && !this.allClients$.closed) {
        this.allClients$.next(client);
        // Update client info and relation details when client changes
        this.clientInfos.next([]);
        this.setClientInfo();
        this.setRelationDetails();
        this.loadStageStatus();
        // Hide skeleton when client data is available
        this.showSkeletonState.set(false);
        if (!this.showSkeleton$().closed) {
          this.showSkeleton$().next(false);
        }
      }
    });
  }

  private initBehaviorSubjectSubscriptions(): void {
    // Subscribe to showSkeleton$ BehaviorSubject to sync with local signal
    const skeletonSubject = this.showSkeleton$();
    if (skeletonSubject && !skeletonSubject.closed) {
      skeletonSubject
        .pipe(takeUntil(this.destroy$))
        .subscribe(value => {
          this.showSkeletonState.set(value);
          this.cd.detectChanges();
        });
    }

    // Subscribe to showCloseIcon$ BehaviorSubject to sync with local signal
    const closeIconSubject = this.showCloseIcon$();
    if (closeIconSubject && !closeIconSubject.closed) {
      closeIconSubject
        .pipe(takeUntil(this.destroy$))
        .subscribe(value => {
          this.showCloseIconState.set(value);
        });
    }
  }

  ngOnInit(): void {
    // Initialize BehaviorSubject subscriptions for syncing with signals
    this.initBehaviorSubjectSubscriptions();

    // Check all feature flags using combineLatest for cleaner code
    // This waits for Statsig initialization and updates all flags at once
    combineLatest([
      this.featureFlagService.getFlag$('client_organisation_link'),
      this.featureFlagService.getFlag$('customer_credit_balance'),
      this.featureFlagService.getFlag$('show_sms'),
      this.featureFlagService.getFlag$('show_email'),
      this.featureFlagService.getFlag$('show_digital_keyfob')
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([clientOrgLink, creditBalance, showSms, showEmail, showDigitalKeyfob]) => {
        this.showOrganization.set(clientOrgLink);
        this.showAddCreditButton.set(creditBalance);
        this.showSendSMs.set(showSms);
        this.showSendEmail.set(showEmail);
        this.showDeleteDigitalPass.set(showDigitalKeyfob);
        this.cd.detectChanges();
      });

    this.store.select(TENANTS_INFO)
      .pipe(takeUntil(this.destroy$))
      .subscribe((tenant: TENANTSINTERFACE) => {
        this.currentTenantId.set(tenant.selected_tenant.id);
        this.allTenants.set(tenant.all_tenant);
        this.selectedTenant.set(tenant.selected_tenant);
      });
    
    // Initialize relation details
    this.setRelationDetails();
  }


  private loadStageStatus() {
    const client = this.client();
    const currentTenantId = this.currentTenantId();
    if (client?.stage_master) {
      const stageMaster = client.stage_master.find(s => s.tenant_id == currentTenantId);
      if (stageMaster) {
        this.store.select(STAGE_MASTER_STATE)
          .pipe(
            map((value: STAGE_MASTER_MODEL[]) => value.find(obj => obj.id == stageMaster?.stage_master_id)?.name_primary_language || null),
            takeUntil(this.destroy$)
          )
          .subscribe({
            next: (status) => {
              this.currentStageStatus.set(status);
              this.cd.detectChanges();
            },
            error: (error) => {
              console.error('Error loading stage status:', error);
              this.currentStageStatus.set(null);
            }
          });
      } else {
        this.currentStageStatus.set(null);
      }
    } else {
      this.currentStageStatus.set(null);
    }
  }

  setClientInfo(){
    this.clientInfos.next([]);
    const client = this.client();
    if(client?.id) {
      const infos: any[] = [{name: 'Client ID', value: client.id.toString()}];
      if(client.current_keyfob) infos.push({name: 'KeyFob ID', value: client.current_keyfob});
      if(client.external_cms_id) infos.push({name: 'External CMS ID', value: client.external_cms_id});
      if(client.external_providers){
        client.external_providers.forEach((provider: ExternalProvider) => {
          infos.push({name: this.getExternalProviderName(provider.category), value: provider.provider_id});
        });
      }
      
      this.clientInfos.next(infos);
      
      if(infos.length > 0) {
        this.selectedClientInfo.set(infos[0]);
      }
    }
    this.cd.detectChanges();
  }

  getExternalProviderName(category: ExternalProviderCategory) {
    return category === ExternalProviderCategory.ClassPass || ExternalProviderCategory.ClassPass.toString() === ExternalProviderCategory[ExternalProviderCategory.ClassPass] ? 'Class Pass ID' : 'Fit Pass ID';
  }

  getClientDetails() {
    const clientId = this.client()?.id;
    if (clientId) {
      this.isLoadingClientDetails.set(true);
      this.showSkeleton$().next(true);
      this.clientService.getClientByIdNew(clientId).subscribe({
        next: (res) => {
          this.allClients$.next(res);
          this.isLoadingClientDetails.set(false);
          this.showSkeleton$().next(false);
          this.setClientInfo();
          this.setRelationDetails();
          this.cd.detectChanges();
        },
        error: (_error: any) => {
          this.isLoadingClientDetails.set(false);
          this.showSkeleton$().next(false);
          this.cd.detectChanges();
        }
      });
    }
  }

  readonly isCurrentTenantClient = computed(() => {
    const client = this.client();
    const tenantId = this.tenantId();
    if (client && client.client_status != null && (client.client_status == ClientStatus.Client || client.client_status.toString() == ClientStatus[ClientStatus.Client])) {
      return tenantId == client.home_club_tenant_id;
    }
    else if (client && client.client_status != null && (client.client_status == ClientStatus.Prospect || client.client_status.toString() == ClientStatus[ClientStatus.Prospect]
    || client.client_status == ClientStatus.Trial || client.client_status.toString() == ClientStatus[ClientStatus.Trial])) {
      return tenantId == client.latest_createdin_tenant_id;
    }
    else {
      if (client && tenantId == client?.latest_createdin_tenant_id && tenantId == client?.home_club_tenant_id) {
        return true;
      }
      else if (client && tenantId == client?.home_club_tenant_id) {
        return true;
      }
      else {
        return false;
      }
    }
  });

  clientStatus(client: any): string {
    if (client?.client_status)
      return this.clientService.getClientStatusHtml(client.client_status.toString());
    else
      return '';
  }

  memberSince(fromDate: string) {
    return timeLineService(this.timeService.ConvertUTCToLocalDateTime(fromDate));
  }

  getStageStatus(): Observable<string> {
    const stageMaster = this.client()?.stage_master?.find(s => s.tenant_id == this.currentTenantId());
    if (!stageMaster?.stage_master_id) {
      return of('');
    }
    return this.store.select(STAGE_MASTER_STATE).pipe(
      map((value: STAGE_MASTER_MODEL[]) => {
        const stage = value.find(obj => obj.id == stageMaster.stage_master_id);
        return stage?.name_primary_language || '';
      })
    );
  }

  isDefaultDate(date: string): boolean {
    const year = new Date(date).getFullYear();
    if (year == 9999 || year == 1) return true;
    else return false;
  }

  isDefaultBirthDayDate(date: string): boolean {
    if(date  == null) return true;
    const year = new Date(date).getFullYear();
    if (year == 9999 || year == 1) return true;
    else return false;
  }

  isProspect(status: any): boolean {
    const cltStatus = ClientStatus[status as keyof typeof ClientStatus];
    return cltStatus == ClientStatus.Prospect || status.toString() == ClientStatus[ClientStatus.Prospect]
  }

  isNonMember(status: any): boolean {
    const cltStatus = ClientStatus[status as keyof typeof ClientStatus];
    return cltStatus == ClientStatus.ClientNonMember || status.toString() == ClientStatus[ClientStatus.ClientNonMember]
  }

  isExClientNonMember(status: any): boolean {
    const cltStatus = ClientStatus[status as keyof typeof ClientStatus];
    return cltStatus == ClientStatus.ExClientNonMember || status.toString() == ClientStatus[ClientStatus.ExClientNonMember]
  }

  hasAnyRelation(client: ClientById): boolean {
    if (!client) return false;
    return client.is_child || (client.childs && client.childs.length > 0);
  }

  setRelationDetails() {
    this.relationDetails.set([]);
    
      const client = this.client();
      if(this.hasAnyRelation(client) && client?.parent_client_id > 0) {
      // For parent client, make async call
      this.clientService.getClientByIdNew(client.parent_client_id).subscribe({
        next: (res: ClientById) => {
          this.parentClient.set(res);
          const clientValue = this.client();
          const details = [{
            relation: clientValue.relation, 
            relationImage: res.image, 
            relationName: res.full_name, 
            relationType: clientValue.relation === 'Child' ? 'Parent' : clientValue.relation
          }];
          this.relationDetails.set(details);
          this.cd.detectChanges();
        },
        error: (error: any) => {
          console.error('Error loading parent client data:', error);
        }
      });
    }
    else if(this.hasAnyRelation(client) && client?.childs?.length > 0) {
      // For child clients, data is already available
      const details: any[] = [];
      for(const child of client.childs) {
        details.push({
          relation: child.relation, 
          relationImage: child.image, 
          relationName: child.full_name, 
          relationType: child.relation
        });
      }
      this.relationDetails.set(details);
      this.cd.detectChanges();
    }
  }

  getRelationDetails() {
    return this.relationDetails();
  }

  crownType(clientSince: string) {
    const ValidDate = new Date(this.timeService.ConvertUTCToLocalDateTime(clientSince));
    if (Number.isNaN(ValidDate.getDate())) {
      return { crown: 'silver-crown', coin: 'less-spent-icon', color: 'text-400' };
    }
    if (this.isDefaultDate(clientSince)) {
      return { crown: 'default', coin: 'default', color: '' };
    }
    const duration = timeLineService(this.timeService.ConvertUTCToLocalDateTime(clientSince));
    if (
      duration.timeline == 'year' &&
      duration.value >= 1 &&
      duration.value < 3 &&
      duration.type == 'ago'
    ) {
      return { crown: 'bronze-crown', coin: 'medium-spent-icon', color: 'text-800' };
    } else if (
      duration.timeline == 'year' &&
      duration.value >= 3 &&
      duration.value < 5 &&
      duration.type == 'ago'
    ) {
      return { crown: 'silver-crown', coin: 'less-spent-icon', color: 'text-400' };
    } else if (
      duration.timeline == 'year' &&
      duration.value >= 5 &&
      duration.type == 'ago'
    ) {
      return { crown: 'gold-crown', coin: 'most-spent-icon', color: 'text-yellow-600' };
    } else {
      return { crown: 'default', coin: 'default', color: '' };
    }
  }

  // Signal to track skeleton state - updated via subscription in constructor
  readonly showSkeletonState = signal(true);
  readonly showSkeleton = computed(() => this.showSkeletonState());

  // Returns the BehaviorSubject for columnData input (used by prospect-list-actions)
  readonly clientDetails = computed(() => this.allClients$);

  changeInAction(e: any) {
    if(e && e.action) this.getClientDetails();
    this.changeEvent.emit(e);
  }

  closeSidebar() {
    this.closeClientSidebar.emit(true);
    this.cd.detectChanges();
  }

  ActiveTenant(client: any) {
    if (client.home_club_tenant_id && client.home_club_tenant_id > 0)
      return this.allTenants().find((o: { id: number; }) => o.id == client.home_club_tenant_id) || null;
    else return this.allTenants().find((o: { id: number; }) => o.id == client.createdin_tenant_id) || null;
  }

  getSalesRep(staffId: number) {
    return this.staffStoreService.getStaffById(staffId).pipe(map((staff) => {
      return staff;
    }));
  }


  getLanguage(code: string) {
    const language = CONSTANTS_DATA.ALL_LANGUAGES.find((lang) => (lang.LANG_CODE === code));
    return language ? language.LANG_ABBRE : '';
  }

  toggleSendEmailDialog() {
    this.SelectedClient.next(this.client());
    this.toggleSendEmailDialog$.next(true);
  }

  closeSendEmailDialog(event: any) {
    this.toggleSendEmailDialog$.next(false);
    if (event?.action === 'updated') this.getClientDetails();
    this.cd.detectChanges();
  }

  toggleCreateSmsDialog() {
    this.SelectedClient.next(this.client());
    this.toggleCreateSmsDialog$.next(true);
  }

  closeCreateSmsDialog(event: any) {
    this.toggleCreateSmsDialog$.next(false);
    if (event?.action === 'updated') this.getClientDetails();
    this.cd.detectChanges();
  }

  toggleSendWhatsAppDialog() {
    this.SelectedClient.next(this.client());
    this.toggleSendWhatsAppDialog$.next(true);
  }

  closeSendWhatsAppDialog(_e: any) {
    this.toggleSendWhatsAppDialog$.next(false);
    this.cd.detectChanges();
  }

  get isClientBlackListed() {
    const client = this.client();
    return client && client.is_blacklisted;
  }

  CloseBookAppointment(event: any) {
    this.showBookAppointmentPopup.set(false);
  }
  
  // copyToClipboard(text: string) {
  //   const textArea = document.createElement('textarea');
  //   textArea.value = text;
  //   document.body.appendChild(textArea);
  //   textArea.select();

copyToClipboard(text: string) {
  // Close popover immediately to avoid it getting stuck when user hits ESC before async copy completes
  const profileActionPopover = this.profileActionPopover();
  if (profileActionPopover) {
    profileActionPopover.hide();
  }

  // Use modern Clipboard API if available, otherwise fallback to execCommand
  if (navigator.clipboard) {
    navigator.clipboard.writeText(text).then(() => {
      this.translate.get('common.message.COPY_SUCCESS').subscribe((message: string) => {
        this.toastMessageService.showSuccess(message);
      });
    }).catch(() => {
      // Fallback to execCommand if clipboard API fails
      this.fallbackCopyToClipboard(text);
    });
  } else {
    // Fallback for older browsers
    this.fallbackCopyToClipboard(text);
  }
}

private fallbackCopyToClipboard(text: string) {
  const textArea = document.createElement('textarea');
  textArea.value = text;
  textArea.style.position = 'fixed';
  textArea.style.left = '-999999px';
  textArea.style.top = '-999999px';
  document.body.appendChild(textArea);
  textArea.focus();
  textArea.select();
  
  try {
      // Using deprecated execCommand as fallback for older browsers
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore - execCommand is deprecated but needed as fallback
      const successful = document.execCommand('copy');
    if (successful) {
      this.translate.get('common.message.COPY_SUCCESS').subscribe((message: string) => {
        this.toastMessageService.showSuccess(message);
      });
    } else {
      throw new Error('Copy command failed');
    }
  } catch (_err) {
    this.toastMessageService.showError('Failed to copy to clipboard');
  } finally {
    // Only remove once, check if it's still a child before removing
    if (textArea.parentNode === document.body) {
      textArea.remove();
    }
    // Popover is already closed at start of copyToClipboard
  }
}

getFlag(language: string){
  const lang = CONSTANTS_DATA.ALL_LANGUAGES.find((lang) => (lang.LANG_CODE === language));
  return lang ? lang.COUNTRY : '';
}


// Signal to track close icon state - updated via subscription
readonly showCloseIconState = signal(true);
readonly showCloseIcon = computed(() => this.showCloseIconState());

onDropdownKeydown(event: KeyboardEvent, popover: Popover): void {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    popover.toggle(event);
  }
}

onCloseKeydown(event: KeyboardEvent): void {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    this.closeSidebar();
  }
}

onCopyKeydown(event: KeyboardEvent, value: string): void {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    this.copyToClipboard(value);
  }
}

onSmsKeydown(event: KeyboardEvent): void {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    this.toggleCreateSmsDialog();
  }
}

onWhatsappKeydown(event: KeyboardEvent, popover: Popover): void {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    popover.toggle(event);
  }
}

onEmailKeydown(event: KeyboardEvent): void {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    this.toggleSendEmailDialog();
  }
}

onBookAppointmentKeydown(event: KeyboardEvent): void {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    this.showBookAppointmentPopup.set(true);
  }
}

onWhatsappTemplateKeydown(event: KeyboardEvent): void {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    this.toggleSendWhatsAppDialog();
  }
}

getClientPreferredLanguageImage(language: string) {
  if(!language) return 'assets/img/flags/us.svg';
  switch (language.toLowerCase()) {
    case 'en':
      return 'assets/img/flags/us.svg';
    case 'ar':
      return 'assets/img/flags/ar.svg';
    case 'hi':
      return 'assets/img/flags/in.svg';
    case 'fr':
      return 'assets/img/flags/fr.svg';
    case 'de':
      return 'assets/img/flags/de.svg';
    case 'it':
      return 'assets/img/flags/it.svg';
    default:
      return 'assets/img/flags/us.svg';
  }
}

ngOnDestroy(): void {
  // Hide popover overlay to prevent it from staying visible when component is destroyed (e.g. ESC closes profile)
  const profileActionPopover = this.profileActionPopover();
  if (profileActionPopover) {
    profileActionPopover.hide();
  }
  // Complete the destroy subject to unsubscribe from all observables
  this.destroy$.next();
  this.destroy$.complete();
}

}
